import React from 'react'
import { useMutation } from '@tanstack/react-query'
import { joinRooms } from '../../../api/chat'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../common/Button'
import { ToastType } from '../../common/Toast/toast-type'
import { useRoomMessagesCache } from './useRoomMessagesCache'

interface JoinBarProps {
  roomId: string
}

export const JoinBar = ({ roomId }: JoinBarProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { invalidateRoom } = useRoomMessagesCache()

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const joined = (await joinRooms({ roomIds: [roomId] })).data.data
      if (!joined.length) throw new Error('Room not joined')
      return joined
    },
    onSuccess: () => {
      invalidateRoom(roomId)
      setToastNotification(
        ToastType.SUCCESS,
        translate('joinGroupChat.flashMessage.joinGroupChatSuccess')
      )
    },
    onError: () => {
      setToastNotification(
        ToastType.DANGER,
        translate('joinGroupChat.flashMessage.joinGroupChatFailure')
      )
    },
  })

  return (
    <div className="bg-neutral-98 p-4 md:py-6 md:px-6">
      <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-neutral-90 bg-neutral-94 py-6">
        <p style={theme.textVariants.base} className="text-neutral-40">
          {translate('joinGroupChat.joinHeader')}
        </p>
        <Button
          data-testid="room-join-button"
          label={translate('joinGroupChat.join')}
          type="button"
          variant={ButtonVariant.PRIMARY}
          onClick={() => mutate()}
          loading={isPending}
        />
      </div>
    </div>
  )
}
